import { useReducer, useMemo, useEffect } from 'react'

import { Account } from '@/core/api/entities/account/account'

interface BrandProfileState {
	selectedBrandTags: number[]
	selectedAgeTags: string[]
	selectedGender: string
	selectedSocioeconomicTags: string[]
}

type Action =
	| { type: 'TOGGLE_BRAND_TAG'; id: number }
	| { type: 'TOGGLE_AGE_TAG'; tag: string }
	| { type: 'SELECT_GENDER'; tag: string }
	| { type: 'TOGGLE_SOCIOECONOMIC_TAG'; tag: string }
	| { type: 'RESET'; initialState: BrandProfileState }

const getInitialState = (account?: Account): BrandProfileState => {
	const demographicAges = account?.demographicAges || []
	const demographicGender = account?.demographicGender || ''
	const demographicSocioeconomics = account?.demographicSocioeconomics || []
	const brandIdentities =
		account?.brandIdentities?.map((identity) => identity.id) || []

	return {
		selectedBrandTags: brandIdentities,
		selectedAgeTags: demographicAges,
		selectedGender: demographicGender,
		selectedSocioeconomicTags: demographicSocioeconomics,
	}
}

const toggleTagInArray = (tags: string[], tag: string): string[] => {
	const isSelected = tags.includes(tag)

	if (isSelected) {
		return tags.filter((t) => t !== tag)
	}

	if (tag === 'indifferent') {
		return ['indifferent']
	}

	return tags.filter((t) => t !== 'indifferent').concat(tag)
}

const toggleBrandIdentity = (ids: number[], id: number): number[] => {
	return ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]
}

const reducer = (
	state: BrandProfileState,
	action: Action
): BrandProfileState => {
	switch (action.type) {
		case 'TOGGLE_BRAND_TAG':
			return {
				...state,
				selectedBrandTags: toggleBrandIdentity(
					state.selectedBrandTags,
					action.id
				),
			}
		case 'TOGGLE_AGE_TAG':
			return {
				...state,
				selectedAgeTags: toggleTagInArray(state.selectedAgeTags, action.tag),
			}
		case 'SELECT_GENDER':
			return {
				...state,
				selectedGender: action.tag,
			}
		case 'TOGGLE_SOCIOECONOMIC_TAG':
			return {
				...state,
				selectedSocioeconomicTags: toggleTagInArray(
					state.selectedSocioeconomicTags,
					action.tag
				),
			}
		case 'RESET':
			return action.initialState
		default:
			return state
	}
}

export const useBrandProfileState = (account?: Account) => {
	const initialState = useMemo(() => getInitialState(account), [account])

	const [state, dispatch] = useReducer(reducer, initialState)

	// Reset state when account changes
	useEffect(() => {
		dispatch({ type: 'RESET', initialState: getInitialState(account) })
	}, [account?.id])

	const reset = () => {
		const currentInitial = getInitialState(account)
		dispatch({ type: 'RESET', initialState: currentInitial })
	}

	return {
		state,
		dispatch,
		reset,
		initialState,
	}
}
